import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { partial } from 'lodash';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';

import { useLabels } from '../../../hooks/useLabels';
import { Icon } from '../../UI';
import {
  COLUMN_CHART_COLORS,
  ColumnsChart,
} from '../HistoricalIndex/ColumnsChart';
import { GridWidget } from '../types';
import { useUserStrategies } from '../../../context/UserStrategies';
import {
  SET_STRATEGY_RISK_ASSET_CONCENTRATION,
  SET_STRATEGY_RISK_CURRENCY_CONCENTRATION,
  SET_STRATEGY_RISK_GEOGRAPHICAL_CONCENTRATION,
  SET_STRATEGY_RISK_INDUSTRY_CONCENTRATION,
} from '../../../http/apollo-queries';
import { useModalContext } from '../../../context/Modal';
import AdvancedSettings from './AdvancedSettings';
import { IStrategy } from '../../../types/model';
import { useErrorHandler } from 'react-error-boundary';
import InfoTooltip from '../../InfoTooltip/Info-tooltip';

interface RiskRateProps extends GridWidget {
  options: {
    strategyId: string;
  };
}

type Risks = {
  risk?: number;
  riskAssetConcentration?: number;
  riskGeographicalConcentration?: number;
  riskCurrencyConcentration?: number;
  riskIndustryConcentration?: number;
};

enum RISK_TYPES {
  'ASSET',
  'GEOGRAPHICAL',
  'CURRENCY',
  'INDUSTRY',
}

const RiskRate: React.FC<RiskRateProps> = ({ width, height, options }) => {
  const strategyId = options.strategyId;
  const { t } = useTranslation();
  const onError = useErrorHandler();

  const { strategies, refetch } = useUserStrategies();
  const { dispatch: dispatchModal } = useModalContext();

  const labels = useLabels();
  const strategy = strategies.find((s) => s.id === strategyId);

  const {
    risk,
    riskAssetConcentration,
    riskGeographicalConcentration,
    riskCurrencyConcentration,
    riskIndustryConcentration,
  } = strategy as IStrategy;

  const [risks, setRisks] = useState<Risks>({
    risk,
    riskAssetConcentration,
    riskGeographicalConcentration,
    riskCurrencyConcentration,
    riskIndustryConcentration,
  });

  useEffect(() => {
    setRisks({
      risk: strategy?.risk,
      riskAssetConcentration: strategy?.riskAssetConcentration,
      riskGeographicalConcentration: strategy?.riskGeographicalConcentration,
      riskCurrencyConcentration: strategy?.riskCurrencyConcentration,
      riskIndustryConcentration: strategy?.riskIndustryConcentration,
    });
  }, [strategy?.risk]);

  const [updateRiskAsset, { loading: loadingAsset }] = useMutation(
    SET_STRATEGY_RISK_ASSET_CONCENTRATION,
    { onError }
  );
  const [updateRiskGeographical, { loading: loadingGeo }] = useMutation(
    SET_STRATEGY_RISK_GEOGRAPHICAL_CONCENTRATION,
    { onError }
  );
  const [updateCurrencyRisk, { loading: loadingCurrency }] = useMutation(
    SET_STRATEGY_RISK_CURRENCY_CONCENTRATION,
    { onError }
  );
  const [updateIndustryRisk, { loading: loadingIndustry }] = useMutation(
    SET_STRATEGY_RISK_INDUSTRY_CONCENTRATION,
    { onError }
  );

  // Destructure risk types from the Strategy object and update the state
  function setRetrievedRisks({
    risk,
    riskAssetConcentration,
    riskGeographicalConcentration,
    riskCurrencyConcentration,
    riskIndustryConcentration,
  }: Risks) {
    setRisks({
      risk,
      riskAssetConcentration,
      riskGeographicalConcentration,
      riskCurrencyConcentration,
      riskIndustryConcentration,
    });
  }

  const handleChartClick = useCallback(
    (type: RISK_TYPES, value: number) => {
      const variables = { strategyId, value };
      switch (type) {
        case RISK_TYPES.ASSET:
          updateRiskAsset({ variables })
            .then(({ data: { setStrategyRiskAssetConcentration } = {} }) =>
              setRetrievedRisks(setStrategyRiskAssetConcentration)
            )
            .catch(() => toast.error(''));
          break;
        case RISK_TYPES.GEOGRAPHICAL:
          updateRiskGeographical({ variables })
            .then(
              ({ data: { setStrategyRiskGeographicalConcentration } = {} }) =>
                setRetrievedRisks(setStrategyRiskGeographicalConcentration)
            )
            .catch(() => toast.error(''));
          break;
        case RISK_TYPES.CURRENCY:
          updateCurrencyRisk({ variables })
            .then(({ data: { setStrategyRiskCurrencyConcentration } = {} }) =>
              setRetrievedRisks(setStrategyRiskCurrencyConcentration)
            )
            .catch(() => toast.error(''));
          break;
        case RISK_TYPES.INDUSTRY:
          updateIndustryRisk({ variables })
            .then(({ data: { setStrategyRiskIndustryConcentration } = {} }) =>
              setRetrievedRisks(setStrategyRiskIndustryConcentration)
            )
            .catch(() => toast.error(''));
          break;
      }
    },
    [
      strategyId,
      updateRiskAsset,
      updateRiskGeographical,
      updateCurrencyRisk,
      updateIndustryRisk,
    ]
  );

  const loading =
    loadingGeo || loadingCurrency || loadingAsset || loadingIndustry;

  const data = [
    {
      title: t('widget.riskRate.assetConcentration'),
      risk: risks.riskAssetConcentration,
      onclick: partial(handleChartClick, RISK_TYPES.ASSET),
      color: COLUMN_CHART_COLORS.DARK_BLUE,
      tooltip: t('widget.riskRate.assetConcentrationTooltip'),
    },
    {
      title: t('widget.riskRate.geographicalConcentration'),
      risk: risks.riskGeographicalConcentration,
      onclick: partial(handleChartClick, RISK_TYPES.GEOGRAPHICAL),
      color: COLUMN_CHART_COLORS.ORANGE,
      tooltip: t('widget.riskRate.geographicalConcentrationTooltip'),
    },
    {
      title: t('widget.riskRate.currencyRisk'),
      risk: risks.riskCurrencyConcentration,
      onclick: partial(handleChartClick, RISK_TYPES.CURRENCY),
      color: COLUMN_CHART_COLORS.TEAL,
      tooltip: t('widget.riskRate.currencyRiskTooltip'),
    },
    {
      title: t('widget.riskRate.industryRisk'),
      risk: risks.riskIndustryConcentration,
      onclick: partial(handleChartClick, RISK_TYPES.INDUSTRY),
      color: COLUMN_CHART_COLORS.DARK_PURPLE,
      tooltip: t('widget.riskRate.industryRiskTooltip'),
    },
  ];

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto scrollbar-hide">
      <div className="border-b border-grey-10 py-7 px-4 dark:border-grey-80">
        <div className={'flex justify-between items-center'}>
          <div className={'flex justify-items-start'}>
            <span className="text-md">
              {t('widget.riskRate.overallStrategyRisk')}
            </span>
            <InfoTooltip
              isTooltipIconVisible
              content={t('widget.riskRate.overallStrategyRiskTooltip')}
              tooltipIconClassName={'ml-1 mt-1'}
            />
          </div>
          <button
            className={'text-xs'}
            onClick={() =>
              dispatchModal({
                element: <AdvancedSettings strategy={strategy as IStrategy} />,
                title: t('widget.riskRate.modal.title'),
              })
            }
          >
            {t('widget.riskRate.advancedSettings')}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-xl">{risks.risk}</h2>{' '}
          <span className="font-medium text-grey-50 text-sm mt-2">
            {labels[Math.round(risks.risk ?? 0) ?? 0]}
          </span>
        </div>
        <div>
          <ColumnsChart
            color={COLUMN_CHART_COLORS.BLACK}
            index={Math.round(risks.risk ?? 0)}
            className="h-12 mt-2 w-80"
          />
        </div>
      </div>
      <div
        className={`px-4 py-5 flex justify-between overflow-x-auto ${
          width === 1 || height === 2
            ? 'flex-col gap-16 items-start'
            : 'flex-row items-center gap-16'
        }`}
      >
        {data.map(({ onclick, risk, title, tooltip, color }, index) => {
          return (
            <div className="flex flex-col" key={index}>
              <div className="flex justify-items-start text-sm truncate">
                <span>{title}</span>
                <InfoTooltip
                  isTooltipIconVisible
                  content={tooltip}
                  tooltipIconClassName={'ml-1'}
                />
              </div>
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-lg">{risk}</h2>{' '}
                <span className="font-medium text-grey-50 mt-2">
                  {labels[risk ?? 0]}
                </span>
              </div>
              <ColumnsChart
                disabled={loading}
                color={color}
                index={risk ?? 0}
                className="h-8 mt-2"
                onClick={(colNum) => colNum !== risk && onclick(colNum)}
              />
              <div className={'flex-none mt-4'}>
                <button
                  className={'font-semibold uppercase text-sm'}
                  onClick={() => onclick(0)}
                >
                  {t('widget.riskRate.reset')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RiskRate;
